import React from 'react'
import { Navigate } from 'react-router-dom'
import type { RouteObject } from 'react-router-dom'
import { AuthGuard } from '@/features/auth/components/AuthGuard'
import { AppLayout } from '@/features/layout/AppLayout'

const DashboardPage = React.lazy(() =>
  import('@/features/dashboard/pages/DashboardPage').then((m) => ({ default: m.DashboardPage }))
)
const AuthCallbackPage = React.lazy(() =>
  import('@/features/auth/pages/AuthCallbackPage').then((m) => ({ default: m.AuthCallbackPage }))
)

const ReadingActivitiesPage = React.lazy(() =>
  import('@/features/activities/pages/ReadingActivitiesPage').then((m) => ({ default: m.ReadingActivitiesPage }))
)
const SorWordsPage = React.lazy(() =>
  import('@/features/science-of-reading/sor-words/SorWordsPage').then((m) => ({ default: m.SorWordsPage }))
)
const SorReadingsPage = React.lazy(() =>
  import('@/features/science-of-reading/readings/pages/SorReadingsPage').then((m) => ({ default: m.SorReadingsPage }))
)
const SorReadingEditPage = React.lazy(() =>
  import('@/features/science-of-reading/readings/pages/SorReadingEditPage').then((m) => ({ default: m.SorReadingEditPage }))
)
const LessonGroupsPage = React.lazy(() =>
  import('@/features/science-of-reading/lessons/pages/LessonGroupsPage').then((m) => ({ default: m.LessonGroupsPage }))
)
const LessonsListPage = React.lazy(() =>
  import('@/features/science-of-reading/lessons/pages/LessonsListPage').then((m) => ({ default: m.LessonsListPage }))
)
const LessonFormPage = React.lazy(() =>
  import('@/features/science-of-reading/lessons/pages/LessonFormPage').then((m) => ({ default: m.LessonFormPage }))
)
const LessonPreviewPage = React.lazy(() =>
  import('@/features/science-of-reading/lessons/pages/LessonPreviewPage').then((m) => ({ default: m.LessonPreviewPage }))
)

// Dictionary worker lazy loaded pages
const DictPhonemesPage = React.lazy(() =>
  import('@/features/dictionary/phonemes/DictPhonemesPage').then((m) => ({ default: m.DictPhonemesPage }))
)
const DictGraphemesPage = React.lazy(() =>
  import('@/features/dictionary/graphemes/DictGraphemesPage').then((m) => ({ default: m.DictGraphemesPage }))
)
const DictWordsPage = React.lazy(() =>
  import('@/features/dictionary/words/DictWordsPage').then((m) => ({ default: m.DictWordsPage }))
)
const PronunciationLabPage = React.lazy(() =>
  import('@/features/pronunciation-lab/pages/PronunciationLabPage').then((m) => ({ default: m.PronunciationLabPage }))
)
const LexicalsPage = React.lazy(() =>
  import('@/features/lexicals/lexicals/pages/LexicalsPage').then((m) => ({ default: m.LexicalsPage }))
)
const LexicalSentencesPage = React.lazy(() =>
  import('@/features/lexicals/sentences/pages/SentencesPage').then((m) => ({ default: m.SentencesPage }))
)
const PassagesPage = React.lazy(() =>
  import('@/features/lexicals/passages/pages/PassagesPage').then((m) => ({ default: m.PassagesPage }))
)
const TaxonomiesPage = React.lazy(() =>
  import('@/features/lexicals/taxonomies/pages/TaxonomiesPage').then((m) => ({ default: m.TaxonomiesPage }))
)
const RoadmapsPage = React.lazy(() =>
  import('@/features/lexicals/roadmaps/pages/RoadmapsPage').then((m) => ({ default: m.RoadmapsPage }))
)
const ContentSentencesPage = React.lazy(() =>
  import('@/features/english-content/sentences/pages/ContentSentencesPage').then((m) => ({ default: m.ContentSentencesPage }))
)
const ContentLexicalsPage = React.lazy(() =>
  import('@/features/english-content/lexicals/pages/ContentLexicalsPage').then((m) => ({ default: m.ContentLexicalsPage }))
)
const ContentTaxonomiesPage = React.lazy(() =>
  import('@/features/english-content/taxonomies/pages/ContentTaxonomiesPage').then((m) => ({ default: m.ContentTaxonomiesPage }))
)
const ContentReadingsPage = React.lazy(() =>
  import('@/features/english-content/readings/pages/ContentReadingsPage').then((m) => ({ default: m.ContentReadingsPage }))
)
const ReadingWorkflowPage = React.lazy(() =>
  import('@/features/workflows/reading/pages/ReadingWorkflowPage').then((m) => ({ default: m.ReadingWorkflowPage }))
)

const SuspenseWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <React.Suspense
    fallback={
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0068FF]" />
      </div>
    }
  >
    {children}
  </React.Suspense>
)

import { ErrorBoundary } from './ErrorBoundary'

export const routeDefinitions: RouteObject[] = [
  {
    path: '/',
    errorElement: <ErrorBoundary />,
    element: (
      <AuthGuard requireAdminAccess>
        <AppLayout />
      </AuthGuard>
    ),
    children: [
      { index: true, element: <SuspenseWrapper><DashboardPage /></SuspenseWrapper> },
      { path: 'reading-activities', element: <SuspenseWrapper><ReadingActivitiesPage /></SuspenseWrapper> },
      { path: 'pronunciation-lab', element: <Navigate to="/pronunciation-lab/sentence" replace /> },
      { path: 'pronunciation-lab/:mode', element: <SuspenseWrapper><PronunciationLabPage /></SuspenseWrapper> },
      // Sound Map / Dictionary Redirects
      { path: 'phonemes', element: <Navigate to="/dict-phonemes" replace /> },
      { path: 'graphemes', element: <Navigate to="/dict-graphemes" replace /> },
      { path: 'words', element: <Navigate to="/dict-words" replace /> },
      // Dictionary routes
      { path: 'dict-phonemes', element: <SuspenseWrapper><DictPhonemesPage /></SuspenseWrapper> },
      { path: 'dict-graphemes', element: <SuspenseWrapper><DictGraphemesPage /></SuspenseWrapper> },
      { path: 'dict-words', element: <SuspenseWrapper><DictWordsPage /></SuspenseWrapper> },
      { path: 'english-content', element: <Navigate to="/english-content/taxonomies" replace /> },
      { path: 'english-content/taxonomies', element: <SuspenseWrapper><ContentTaxonomiesPage /></SuspenseWrapper> },
      { path: 'english-content/lexicals', element: <SuspenseWrapper><ContentLexicalsPage /></SuspenseWrapper> },
      { path: 'english-content/sentences', element: <SuspenseWrapper><ContentSentencesPage /></SuspenseWrapper> },
      { path: 'english-content/readings', element: <SuspenseWrapper><ContentReadingsPage /></SuspenseWrapper> },
      { path: 'workflows/reading', element: <SuspenseWrapper><ReadingWorkflowPage /></SuspenseWrapper> },
      { path: 'lexicals', element: <Navigate to="/lexicals/passages" replace /> },
      { path: 'lexicals/passages', element: <SuspenseWrapper><PassagesPage /></SuspenseWrapper> },
      { path: 'passage/:id', element: <SuspenseWrapper><PassagesPage /></SuspenseWrapper> },
      { path: 'lexicals/sentences', element: <SuspenseWrapper><LexicalSentencesPage /></SuspenseWrapper> },
      { path: 'lexicals/items', element: <SuspenseWrapper><LexicalsPage /></SuspenseWrapper> },
      { path: 'lexicals/taxonomies', element: <SuspenseWrapper><TaxonomiesPage /></SuspenseWrapper> },
      { path: 'lexicals/roadmaps', element: <SuspenseWrapper><RoadmapsPage /></SuspenseWrapper> },
      { path: 'lexicals/readings', element: <Navigate to="/lexicals/passages" replace /> },
      { path: 'sor-words', element: <SuspenseWrapper><SorWordsPage /></SuspenseWrapper> },
      { path: 'sor-readings', element: <SuspenseWrapper><SorReadingsPage /></SuspenseWrapper> },
      { path: 'sor-readings/:id/edit', element: <SuspenseWrapper><SorReadingEditPage /></SuspenseWrapper> },
      { path: 'sor-lessons', element: <SuspenseWrapper><LessonGroupsPage /></SuspenseWrapper> },
      { path: 'sor-lessons/:groupId', element: <SuspenseWrapper><LessonsListPage /></SuspenseWrapper> },
      { path: 'sor-lessons/:groupId/lessons/new', element: <SuspenseWrapper><LessonFormPage /></SuspenseWrapper> },
      { path: 'sor-lessons/:groupId/lessons/:lessonId/edit', element: <SuspenseWrapper><LessonFormPage /></SuspenseWrapper> },
      { path: 'sor-lessons/:groupId/lessons/:lessonId/preview', element: <SuspenseWrapper><LessonPreviewPage /></SuspenseWrapper> },
    ],
  },
  {
    path: '/auth/callback',
    element: <SuspenseWrapper><AuthCallbackPage /></SuspenseWrapper>,
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]
